import { CreateParams, GetListParams, GetOneParams, DeleteParams, UpdateParams, GetManyParams } from 'react-admin'
import { EQueryParam, TMeta, ERole, ERoleRequest } from '@/types/data'
import { axiosDZAuthInstance } from './axios'
import { DASHBOARD } from './routes'
import { isArray, omit } from 'underscore'
import type { AxiosRequestConfig } from 'axios'

import { EResource } from '@/admin/types'

// Backend types for error response structure
interface ValidationConstraints {
  [key: string]: string
}

interface ValidationError {
  property: string
  value: string
  target: unknown
  children: unknown[]
  constraints: ValidationConstraints
}

interface ApiErrorResponse {
  statusCode: number
  message: string | ValidationError[]
  error: string
}

const trimmer = (_key: string, value: any) => {
  if (typeof value === 'string') {
    return value.trim()
  }
  return value
}

export const formatErrorMessage = (message: ApiErrorResponse['message']): string => {
  if (typeof message === 'string') return message

  if (Array.isArray(message)) {
    const errorMessages: string[] = []

    message.forEach((errorObj: ValidationError) => {
      if (errorObj.constraints && typeof errorObj.constraints === 'object') {
        // Extract all constraint messages from the constraints object
        const constraintMessages = Object.values(errorObj.constraints)
        errorMessages.push(...constraintMessages)
      }
    })

    return errorMessages.length > 0 ? errorMessages.join(', ') : 'Unknown validation error'
  }

  return String(message)
}

/** Maps Nest class-validator payload (message array) to field keys for forms. */
export const mapValidationErrorsByProperty = (message: ApiErrorResponse['message']): Record<string, string> => {
  const errors: Record<string, string> = {}
  if (typeof message === 'string' || !Array.isArray(message)) {
    return errors
  }
  for (const item of message as ValidationError[]) {
    const prop = item.property
    if (!prop || !item.constraints || typeof item.constraints !== 'object') continue
    const texts = Object.values(item.constraints).filter(Boolean)
    if (texts.length > 0) {
      errors[prop] = texts.join(' ')
    }
  }
  return errors
}

export const paramsMapper = ({ filter, pagination, sort }: GetListParams) => {
  const page = pagination?.page || 1
  const perPage = pagination?.perPage || 10
  const field = sort?.field || 'id'
  const order = sort?.order || 'ASC'

  const sortBy = field

  return {
    [EQueryParam.Offset]: (page - 1) * perPage,
    [EQueryParam.Limit]: perPage,
    [EQueryParam.SortBy]: sortBy,
    [EQueryParam.SortingDirection]: order.toLowerCase(),
    ...filter,
  }
}

export const responseMapper = <T = any>(
  response: T[] | { data: T[]; meta: TMeta },
): { data: T[]; total: number; meta?: { totalCountCapped: boolean } } => {
  const data = Array.isArray(response) ? response : response.data
  const total = !Array.isArray(response) ? response.meta.totalCount : response.length
  const totalCountCapped = !Array.isArray(response) ? response.meta.totalCountCapped : undefined

  // react-admin exposes `meta` to the list through useListContext().meta
  return totalCountCapped === undefined ? { data, total } : { data, total, meta: { totalCountCapped } }
}
export const getCustomList = async (resource: string, params: GetListParams, config?: AxiosRequestConfig) => {
  const axiosInstance = axiosDZAuthInstance
  const { data } = await axiosInstance.get(`${DASHBOARD()}/${resource}`, { params: paramsMapper(params), ...config })
  return responseMapper(data)
}

export const getCustomOne = async (resource: string, params: GetOneParams) => {
  const axiosInstance = axiosDZAuthInstance
  const { id } = params
  const { data } = await axiosInstance.get(`${DASHBOARD()}/${resource}/${id}`)
  return { data }
}

export const getCustomMany = async (resource: string, params: GetManyParams) => {
  const axiosInstance = axiosDZAuthInstance
  const { data } = await axiosInstance.get(`${DASHBOARD()}/${resource}`, { params })
  return responseMapper(data)
}

export const customCreate = async (resource: string, params: CreateParams) => {
  const axiosInstance = axiosDZAuthInstance
  const resData: {
    data: any
  } = {
    data: {
      data: {},
    },
  }

  const trimmedData = JSON.parse(JSON.stringify(params.data, trimmer))
  let finalData: any = {}
  if (resource === EResource.Admins) {
    Object.keys(trimmedData).forEach((key) => {
      const value = trimmedData[key]
      if (value !== '') {
        finalData[key] = trimmedData[key]
      }
    })
  } else {
    finalData = trimmedData
  }

  try {
    const { data } = await axiosInstance.post(`${DASHBOARD()}/${resource}`, finalData)

    resData.data = { id: data.createdId, ...data }
  } catch (error: any) {
    const errorData: ApiErrorResponse = error.response.data
    const formattedMessage = formatErrorMessage(errorData.message)
    throw new Error(formattedMessage)
  }

  return resData
}

export const customDelete = async (resource: string, params: DeleteParams) => {
  const axiosInstance = axiosDZAuthInstance
  const { id } = params
  const resData: {
    data: any
  } = {
    data: {
      data: {},
      id,
    },
  }

  try {
    const { data } = await axiosInstance.delete(`${DASHBOARD()}/${resource}/${id}`)
    resData.data = {
      data,
      id,
    }
  } catch (error: any) {
    const errorData: ApiErrorResponse = error.response.data
    const formattedMessage = formatErrorMessage(errorData.message)
    throw new Error(formattedMessage)
  }

  return resData
}

export const adminsUpdate = async (resource: string, params: UpdateParams) => {
  const axiosInstance = axiosDZAuthInstance
  const { id, previousData, data } = params
  const resData: {
    data: any
  } = {
    data: {
      data: {},
      id,
    },
  }

  const updatedValues = omit(data, (v, k) => {
    if (![ERole.Supplier, ERoleRequest.Supplier].includes(data.role) && isArray(v)) {
      return true
    }
    return previousData[k] === v
  })

  try {
    const { data } = await axiosInstance.put(
      `${DASHBOARD()}/${resource}/${id}`,
      JSON.parse(JSON.stringify(updatedValues, trimmer)),
    )
    resData.data = {
      data,
      id,
    }
  } catch (error: any) {
    const errorData: ApiErrorResponse = error.response.data
    const formattedMessage = formatErrorMessage(errorData.message)
    throw new Error(formattedMessage)
  }

  return resData
}

export const customUpdate = async (resource: string, params: UpdateParams) => {
  const axiosInstance = axiosDZAuthInstance
  const { id } = params
  const resData: {
    data: any
  } = {
    data: {
      data: {},
      id,
    },
  }

  try {
    const { data } = await axiosInstance.put(
      `${DASHBOARD()}/${resource}/${id}`,
      JSON.parse(JSON.stringify(params.data, trimmer)),
    )
    resData.data = {
      data,
      id,
    }
  } catch (error: any) {
    const errorData: ApiErrorResponse = error.response.data
    const formattedMessage = formatErrorMessage(errorData.message)
    throw new Error(formattedMessage)
  }

  return resData
}

export const dateTimeZoneFormatters = {
  parse: (value: string) => {
    if (/([+-]\d{2}:\d{2}|Z)$/.test(value)) {
      return value
    }
    return `${value}+03:00`
  },
  format: (value: string | Date | null | undefined) => {
    if (!value) return ''

    const isoString: string = value instanceof Date ? value.toISOString() : value

    // Convert string into a Date
    const date = new Date(isoString)

    // We want the time *in UTC+3* regardless of the user's system timezone
    const utc = date.getTime()

    // Add +3 hours offset
    const plus3 = new Date(utc + 3 * 60 * 60 * 1000)

    const y = plus3.getUTCFullYear()
    const m = String(plus3.getUTCMonth() + 1).padStart(2, '0')
    const d = String(plus3.getUTCDate()).padStart(2, '0')
    const h = String(plus3.getUTCHours()).padStart(2, '0')
    const min = String(plus3.getUTCMinutes()).padStart(2, '0')

    return `${y}-${m}-${d}T${h}:${min}`
  },
}
